import JxFormDraggableBase from './JxFormDraggableBase';
import { subscribe } from '../../services/utils/pubsub';

/**
 * JxForm — the standard Jx dialog base: Joi-schema validation + drag-to-move + open-focus.
 * Chain: JxFormValidationBase → JxFormDraggableBase → JxForm.
 * Same chain shape as the pre-2019 KDesk8 `DraggableForm extends Form` — validation is the
 * foundation, drag is layered on top, open-focus is layered on top of that. This is the
 * base ~95% of Jx dialogs should extend.
 *
 * extend this, set `this.schema` and `this.name` in the constructor, and you get everything
 * from JxFormDraggableBase (which itself has everything from JxFormValidationBase) PLUS:
 *   - open-focus, two ways depending on WHEN the trigger happens:
 *     1. `focusFieldId` prop — for the common case where the same handler that opens the
 *        dialog (setOpen(true)) also wants to focus a field. IMPORTANT: most Jx dialogs stay
 *        mounted permanently (render() returns null when !open, the instance itself never
 *        unmounts) — so componentDidMount only fires ONCE, on initial page load, while open
 *        is still false and the field doesn't exist in the DOM yet. So this is read in
 *        componentDidUpdate on the false→true `open` transition (covered every time the
 *        dialog opens), with a componentDidMount fallback for the case where a dialog starts
 *        out already open.
 *     2. publish(`${this.name}.OnOpen`, fieldId) — for triggers from OUTSIDE this dialog's own
 *        open/close flow (another already-open dialog, a list row, etc. while THIS dialog is
 *        already open). The subscription itself only registers once (componentDidMount runs
 *        once, see above) but that's fine here — the listener just stays registered and fires
 *        whenever publish() is called later, regardless of when. Do not use this for the
 *        open-time case, use the focusFieldId prop instead — it's simpler and avoids depending
 *        on global pubsub for something that's really just "this dialog, this open() call".
 *   this.name defaults to the class name (this.constructor.name) if not set.
 *
 * NOTE for subclasses: if you override componentDidUpdate, you MUST call
 * super.componentDidUpdate(prevProps) or the focusFieldId open-transition logic breaks silently.
 */
export default class JxForm extends JxFormDraggableBase {
    _unsubscribeOnOpen = null;

    // constructor(props) {
    //     super(props);
    //     this.name = 'EditNoteDlg';
    //     this.schema = { /* ... */ };
    // }

    componentDidMount() {
        try {
            if (this.mounted) return;
            this.mounted = true;

            super.componentDidMount(); // runs JxFormDraggableBase's initial-position setup

            window.addEventListener('keydown', this._handleKeyDown);

            const topic = `${this.getId()}.OnOpen`;
            this._unsubscribeOnOpen = subscribe(topic, this._handleOnOpenFocus);

            // If dialog mounts already open (e.g. via JxWindowManager paint) — bring to front and focus
            if (this.props.open) {
                this.bringToTop();
                if (this.props.focusFieldId) this._handleOnOpenFocus(this.props.focusFieldId);
            }
        } catch (error) {
            this.logError('componentDidMount', `${error.message}`);
        }
    }

    componentDidUpdate(prevProps) {
        if (this.props.open && !prevProps.open) {
            this.bringToTop();
            if (this.props.focusFieldId) this._handleOnOpenFocus(this.props.focusFieldId);
        }
    }

    componentWillUnmount() {
        try {
            if (!this.mounted) return;
            this.mounted = false;

            window.removeEventListener('keydown', this._handleKeyDown);

            if (this._unsubscribeOnOpen) this._unsubscribeOnOpen = this._unsubscribeOnOpen();
        } catch (error) {
            this.logError('componentWillUnmount', `${error.message}`);
        }

        if (super.componentWillUnmount) super.componentWillUnmount();
    }

    _handleKeyDown = (e) => {
        if (!this.isActive()) return;

        if (e.key !== 'Escape') return;
        if (!this.props.open) return;
        //   if (this.state.zIndex !== _topZIndex) return;

        // cancel window
        this.handleClose(-1);
    };

    /**
     * publish(`${this.name}.OnOpen`, 'someFieldId') to focus+select that field.
     * @param {string} fieldId - DOM id of the input to focus
     */
    _handleOnOpenFocus = async (fieldId) => {
        if (!fieldId) return;

        await new Promise((resolve) => setTimeout(resolve, 200)); // let the dialog finish mounting/animating in first
        const input = document.getElementById(fieldId);
        if (!input) {
            this.logDebug(`_handleOnOpenFocus`, `fieldId not found: ${fieldId}`);
            return;
        }

        input.focus();
        input.select?.();
    };
}
